import React, { useContext } from 'react'
import {
  AgxBodyText,
  AgxColumn,
  AgxDivider,
  AgxLabel,
  AgxRow,
  cleanFullAddress,
  DocumentTypesMap,
  Images,
  PropertyAddress,
} from '@urbanx/agx-ui-components'
import type {
  StaffContractDetailModel,
  UploadedDocument,
} from '@urbanx/agx.campaigns.client'
import { ContentLayout, PageLayout } from 'layout'
import { MenuTitles } from 'constants/menu'
import useSelectedAgency from 'hooks/useSelectedAgency'
import useSelectedCampaign from 'hooks/useSelectedCampaign'
import useSelectedContract from 'hooks/useSelectedContract'
import { formatFileSize } from 'utils/formatFileSize'
import { openFileInNewTab } from 'utils/openInNewTab'
import './ContractFiles.scss'
import { AuthContext } from '@urbanx/agx.auth.client'

type ContractFileRow = {
  key: string
  label: string
  document: UploadedDocument
}

const ContractFiles = () => {
  const { getAuthToken, userAccount } = useContext(AuthContext)
  const { agency } = useSelectedAgency()
  const { campaign } = useSelectedCampaign()
  const { contract } = useSelectedContract()
  const contractDetail = contract as StaffContractDetailModel | undefined

  const getDocumentTypeLabel = (documentType: string) =>
    DocumentTypesMap[documentType as keyof typeof DocumentTypesMap] ??
    documentType

  const contractFiles: ContractFileRow[] = [
    ...(contractDetail?.documents?.map((document) => ({
      key: `${document.documentId}-${document.documentType}-${document.containerFilePath}`,
      label: getDocumentTypeLabel(document.documentType ?? ''),
      document,
    })) ?? []),
  ]

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as unknown as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.FILES}
        isContracts={true}
      >
        <AgxColumn extraClasses="panelContainer files-panel" fill>
          <AgxRow extraClasses="panelTitleContainer">
            <AgxLabel medium extraClasses="left-header">
              Contract Files
            </AgxLabel>
            <AgxLabel medium extraClasses="right-header">
              Date Uploaded
            </AgxLabel>
          </AgxRow>
          {contractFiles.length > 0 &&
            contractFiles.map(({ key, label, document }, innerIndex) => (
              <React.Fragment key={key}>
                <React.Fragment>
                  {innerIndex > 0 && <AgxDivider />}
                  <AgxLabel medium>{label}</AgxLabel>
                </React.Fragment>
                <AgxRow key={key}>
                  <AgxColumn extraClasses="file-document">
                    <div
                      className="file-document-container"
                      onClick={() => openFileInNewTab(getAuthToken, document?.containerFilePath)}
                    >
                      <Images.FileTextOutline className="file-document-leftIcon" />
                      <AgxBodyText medium extraClasses="file-document-body">
                        <span className="file-document-name">
                          {document.fileName}
                          <span className="file-document-size">
                            {formatFileSize(document.fileSizeInBytes ?? 0)}
                          </span>
                        </span>
                      </AgxBodyText>
                      <Images.Download className="file-document-download" />
                    </div>
                  </AgxColumn>
                  <AgxColumn extraClasses="file-document-date">
                    <AgxBodyText>
                      <span>
                        {document.dateUploaded
                          ? new Date(document.dateUploaded).toLocaleString(
                              'en-AU'
                            )
                          : ''}
                      </span>
                    </AgxBodyText>
                  </AgxColumn>
                </AgxRow>
              </React.Fragment>
            ))}
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  )
}

export default ContractFiles
